import { supabase } from './supabase';
import { getPhaseColor, getStageColor } from './scheduleUtils';
import { getBuiltInSchedulePreset } from './projectPresets';
import type { BuiltInSchedulePreset, SchedulePresetItem } from './projectPresets/types';

export interface ScheduleSetupResult {
  success: boolean;
  itemsCreated: number;
  linksCreated: number;
  message: string;
  warning?: string;
  alreadyExists?: boolean;
}

export interface ScheduleSetupOptions {
  projectId: string;
  projectType: string;
  createDefaultSchedule: boolean;
  includeDependencyLinks: boolean;
  generateInitialTimeline: boolean;
  scheduleStartDate?: string;
  /** When set, applies this template instead of the project-type default. */
  scheduleTemplateId?: string | null;
}

const BATCH_SIZE = 100;

// Fix 10: These template item IDs correspond to removed draft items (task_no 27,31,34,40,43)
const REMOVED_TEMPLATE_ITEM_IDS = new Set([
  '74b9684c-9d7a-4512-9b43-5f70d7b37393',
  '653704c8-8851-4377-ac6b-8506514d31e0',
  '5c33febc-8558-49a1-b42a-4bddf5d9bb7b',
  'f8718f7d-dd11-497d-b971-815817e56f7e',
  'c5eb1b02-4eee-4408-97d2-b3598d9290bf',
]);

const PLANNING_KEYWORDS = /planning|precon|pre-con|consent|application|resource set|contract|deposit|health.?safety|h&s/i;
const HANDOVER_KEYWORDS = /handover|practical.?completion|ccc|warranty|defect|final.?inspection|final.?clean/i;
const CONSENT_KEYWORDS = /consent|council|inspection|approval|compliance/i;

type TemplateItemRow = {
  id: string;
  sort_order: number;
  default_task_no: number;
  title: string;
  phase?: string | null;
  default_duration_days?: number | null;
  default_is_included?: boolean | null;
  default_assigned_to?: string | null;
  default_assigned_role?: string | null;
  notes?: string | null;
};

type TemplateLinkRow = {
  id: string;
  predecessor_item_id: string;
  successor_item_id: string;
  link_type: string;
  lag_days?: number | null;
};

function resolveMainStage(title: string, phase?: string | null): string {
  const titleLower = (title || '').toLowerCase();
  const phaseLower = (phase || '').toLowerCase();

  if (phaseLower === 'planning' || PLANNING_KEYWORDS.test(titleLower)) return 'Planning';
  if (phaseLower === 'consent' || CONSENT_KEYWORDS.test(titleLower)) return 'Consent';
  if (phaseLower === 'handover' || HANDOVER_KEYWORDS.test(titleLower)) return 'Handover';
  return 'Construction';
}

function buildScheduleItemInsert(
  projectIdText: string,
  item: TemplateItemRow | SchedulePresetItem,
  sourceTemplateItemId: string | null
) {
  const mainStage = resolveMainStage(item.title, item.phase);
  const ganttColor = item.phase ? getPhaseColor(item.phase) : getStageColor(mainStage, null);
  const durationDays = 'default_duration_days' in item && item.default_duration_days != null
    ? item.default_duration_days
    : 1;

  return {
    project_id: projectIdText,
    source_template_item_id: sourceTemplateItemId,
    task_no: item.default_task_no,
    sort_order: item.sort_order,
    title: item.title,
    phase: item.phase || mainStage,
    main_stage: mainStage,
    duration_days: durationDays,
    start_date: null as string | null,
    end_date: null as string | null,
    progress: 0,
    is_complete: false,
    is_included: item.default_is_included !== false,
    assigned_to: 'default_assigned_to' in item ? (item.default_assigned_to || null) : null,
    assigned_role: item.default_assigned_role || null,
    notes: item.notes || null,
    gantt_color: ganttColor,
  };
}

async function batchInsertScheduleItems(
  inserts: ReturnType<typeof buildScheduleItemInsert>[]
): Promise<{ data: Array<{ id: string; source_template_item_id: string | null; task_no: number | null }>; error: string | null }> {
  const allInserted: Array<{ id: string; source_template_item_id: string | null; task_no: number | null }> = [];

  for (let i = 0; i < inserts.length; i += BATCH_SIZE) {
    const chunk = inserts.slice(i, i + BATCH_SIZE);
    const { data, error } = await supabase
      .from('jphph_project_schedule_items')
      .insert(chunk)
      .select('id, source_template_item_id, task_no');

    if (error) {
      return { data: allInserted, error: error.message };
    }
    if (data) allInserted.push(...data);
  }

  return { data: allInserted, error: null };
}

async function projectHasScheduleItems(projectIdText: string): Promise<{ exists: boolean; error?: string }> {
  const { data, error } = await supabase
    .from('jphph_project_schedule_items')
    .select('id')
    .eq('project_id', projectIdText)
    .limit(1);

  if (error) {
    return { exists: false, error: error.message };
  }
  return { exists: !!(data && data.length > 0) };
}

async function insertDefaultScheduleSettings(projectIdText: string): Promise<void> {
  try {
    const { error } = await supabase
      .from('jphph_project_schedule_settings')
      .insert({
        project_id: projectIdText,
        working_days: [1, 2, 3, 4, 5, 6],
        sunday_off: true,
        saturday_off: true,
        exclude_public_holidays: true,
        holiday_country: 'NZ',
        holiday_region: null,
      });
    if (error) console.warn('[ScheduleSetup] Settings insert error:', error);
  } catch (settingsErr) {
    console.warn('[ScheduleSetup] Settings insert exception:', settingsErr);
  }
}

async function applyBuiltInPreset(
  projectIdText: string,
  preset: BuiltInSchedulePreset,
  includeDependencyLinks: boolean
): Promise<ScheduleSetupResult> {
  const scheduleItemInserts = preset.items.map((item) =>
    buildScheduleItemInsert(projectIdText, item, null)
  );

  const { data: inserted, error: insertErr } = await batchInsertScheduleItems(scheduleItemInserts);
  if (insertErr) {
    return {
      success: false,
      itemsCreated: inserted.length,
      linksCreated: 0,
      message: 'Schedule setup failed.',
      warning: `Failed to create schedule items: ${insertErr}`,
    };
  }

  const localToProjectId = new Map<string, string>();
  const byTaskNo = new Map<number, string>();
  for (const row of inserted) {
    if (row.task_no != null) byTaskNo.set(row.task_no, row.id);
  }
  for (const presetItem of preset.items) {
    const projectId = byTaskNo.get(presetItem.default_task_no);
    if (projectId) localToProjectId.set(presetItem.id, projectId);
  }

  let linksCreated = 0;
  if (includeDependencyLinks && preset.links.length > 0) {
    const linkInserts = preset.links
      .map((l) => {
        const predId = localToProjectId.get(l.predecessor_id);
        const succId = localToProjectId.get(l.successor_id);
        if (!predId || !succId) return null;
        return {
          project_id: projectIdText,
          predecessor_item_id: predId,
          successor_item_id: succId,
          link_type: l.link_type,
          lag_days: l.lag_days || 0,
        };
      })
      .filter(Boolean) as Array<{
        project_id: string;
        predecessor_item_id: string;
        successor_item_id: string;
        link_type: string;
        lag_days: number;
      }>;

    if (linkInserts.length > 0) {
      const { error: linkInsertErr } = await supabase
        .from('jphph_project_schedule_links')
        .insert(linkInserts);
      if (linkInsertErr) {
        await insertDefaultScheduleSettings(projectIdText);
        return {
          success: true,
          itemsCreated: inserted.length,
          linksCreated: 0,
          message: `Project created with ${preset.name} schedule.`,
          warning: `Schedule items were created, but dependency links could not be copied: ${linkInsertErr.message}.`,
        };
      }
      linksCreated = linkInserts.length;
    }
  }

  await insertDefaultScheduleSettings(projectIdText);

  const itemWord = inserted.length === 1 ? 'item' : 'items';
  const linkWord = linksCreated === 1 ? 'link' : 'links';

  return {
    success: true,
    itemsCreated: inserted.length,
    linksCreated,
    message: `Created ${preset.name} schedule with ${inserted.length} schedule ${itemWord}${linksCreated > 0 ? ` and ${linksCreated} dependency ${linkWord}` : ''}.`,
  };
}

async function loadActiveScheduleTemplate(
  projectType: string,
  scheduleTemplateId?: string | null
): Promise<{ id: string; name: string; project_type: string; description: string | null } | null> {
  if (scheduleTemplateId) {
    const { data, error } = await supabase
      .from('jphph_schedule_templates')
      .select('id, name, project_type, description')
      .eq('id', scheduleTemplateId)
      .eq('is_active', true)
      .maybeSingle();
    if (error) {
      console.error('[ScheduleSetup] Template by id error:', error);
      return null;
    }
    return data as { id: string; name: string; project_type: string; description: string | null } | null;
  }

  const { data: defaultTpl, error: defaultErr } = await supabase
    .from('jphph_schedule_templates')
    .select('id, name, project_type, description')
    .eq('project_type', projectType)
    .eq('is_active', true)
    .eq('is_default', true)
    .limit(1)
    .maybeSingle();

  if (!defaultErr && defaultTpl) {
    return defaultTpl as { id: string; name: string; project_type: string; description: string | null };
  }

  const { data: templates, error: templateErr } = await supabase
    .from('jphph_schedule_templates')
    .select('id, name, project_type, description')
    .eq('project_type', projectType)
    .eq('is_active', true)
    .order('created_at', { ascending: false })
    .limit(1);

  if (templateErr) {
    console.error('[ScheduleSetup] Template query error:', templateErr);
    return null;
  }

  if (!templates || templates.length === 0) return null;
  return templates[0] as { id: string; name: string; project_type: string; description: string | null };
}

async function applyDatabaseTemplate(
  projectIdText: string,
  projectType: string,
  includeDependencyLinks: boolean,
  scheduleTemplateId?: string | null
): Promise<ScheduleSetupResult | null> {
  const template = await loadActiveScheduleTemplate(projectType, scheduleTemplateId);

  if (!template) {
    if (scheduleTemplateId) {
      return {
        success: false,
        itemsCreated: 0,
        linksCreated: 0,
        message: 'Schedule template not found.',
        warning: 'The selected schedule template could not be loaded.',
      };
    }
    return null;
  }

  const { data: templateItems, error: itemsErr } = await supabase
    .from('jphph_schedule_template_items')
    .select('*')
    .eq('template_id', template.id)
    .order('sort_order', { ascending: true });

  if (itemsErr) {
    return {
      success: false,
      itemsCreated: 0,
      linksCreated: 0,
      message: 'Failed to load template items.',
      warning: itemsErr.message,
    };
  }

  if (!templateItems || templateItems.length === 0) {
    return {
      success: false,
      itemsCreated: 0,
      linksCreated: 0,
      message: 'Template has no schedule items.',
      warning: `${template.name} template has no schedule items.`,
    };
  }

  const filteredTemplateItems = (templateItems as TemplateItemRow[]).filter(
    (t) => !REMOVED_TEMPLATE_ITEM_IDS.has(t.id)
  );

  let templateLinks: TemplateLinkRow[] = [];
  if (includeDependencyLinks) {
    const { data: linksData, error: linksErr } = await supabase
      .from('jphph_schedule_template_links')
      .select('*')
      .eq('template_id', template.id);
    if (linksErr) {
      console.error('[ScheduleSetup] Template links query error:', linksErr);
    } else {
      templateLinks = (linksData || []) as TemplateLinkRow[];
    }
  }

  const filteredTemplateLinks = templateLinks.filter(
    (l) => !REMOVED_TEMPLATE_ITEM_IDS.has(l.predecessor_item_id) && !REMOVED_TEMPLATE_ITEM_IDS.has(l.successor_item_id)
  );

  const scheduleItemInserts = filteredTemplateItems.map((t) =>
    buildScheduleItemInsert(projectIdText, t, t.id)
  );

  const { data: inserted, error: insertErr } = await batchInsertScheduleItems(scheduleItemInserts);
  if (insertErr) {
    return {
      success: false,
      itemsCreated: inserted.length,
      linksCreated: 0,
      message: 'Failed to create schedule items.',
      warning: insertErr,
    };
  }

  const templateToProjectId = new Map<string, string>();
  for (const row of inserted) {
    if (row.source_template_item_id) {
      templateToProjectId.set(row.source_template_item_id, row.id);
    }
  }

  let linksCreated = 0;
  if (includeDependencyLinks && filteredTemplateLinks.length > 0) {
    const linkInserts = filteredTemplateLinks
      .map((l) => {
        const predId = templateToProjectId.get(l.predecessor_item_id);
        const succId = templateToProjectId.get(l.successor_item_id);
        if (!predId || !succId) return null;
        return {
          project_id: projectIdText,
          predecessor_item_id: predId,
          successor_item_id: succId,
          link_type: l.link_type,
          lag_days: l.lag_days || 0,
        };
      })
      .filter(Boolean) as Array<{
        project_id: string;
        predecessor_item_id: string;
        successor_item_id: string;
        link_type: string;
        lag_days: number;
      }>;

    if (linkInserts.length > 0) {
      const { error: linkInsertErr } = await supabase
        .from('jphph_project_schedule_links')
        .insert(linkInserts);
      if (linkInsertErr) {
        await insertDefaultScheduleSettings(projectIdText);
        return {
          success: true,
          itemsCreated: inserted.length,
          linksCreated: 0,
          message: `Created ${template.name} schedule.`,
          warning: `Schedule items were created, but dependency links could not be copied: ${linkInsertErr.message}.`,
        };
      }
      linksCreated = linkInserts.length;
    }
  }

  await insertDefaultScheduleSettings(projectIdText);

  const itemWord = inserted.length === 1 ? 'item' : 'items';
  const linkWord = linksCreated === 1 ? 'link' : 'links';

  return {
    success: true,
    itemsCreated: inserted.length,
    linksCreated,
    message: `Created ${template.name} schedule with ${inserted.length} schedule ${itemWord}${linksCreated > 0 ? ` and ${linksCreated} dependency ${linkWord}` : ''}.`,
  };
}

/**
 * After a project is created, optionally copy an active schedule template
 * into the project's schedule tables. Falls back to built-in presets when
 * no DB template exists (e.g. Renovation). Never throws.
 */
export async function setupProjectSchedule(
  options: ScheduleSetupOptions
): Promise<ScheduleSetupResult> {
  const {
    projectId,
    projectType,
    createDefaultSchedule,
    includeDependencyLinks,
    scheduleTemplateId,
  } = options;

  const projectIdText = String(projectId);

  if (!createDefaultSchedule) {
    return {
      success: true,
      itemsCreated: 0,
      linksCreated: 0,
      message: 'Project created. You can create a schedule later from the Schedule tab.',
    };
  }

  if ((projectType === 'Renovation' || projectType === 'Other') && !scheduleTemplateId) {
    return {
      success: true,
      itemsCreated: 0,
      linksCreated: 0,
      message: 'Project created. Add schedule tasks manually from the Schedule tab.',
    };
  }

  try {
    const existing = await projectHasScheduleItems(projectIdText);
    if (existing.error) {
      return {
        success: false,
        itemsCreated: 0,
        linksCreated: 0,
        message: 'Could not verify existing schedule.',
        warning: `Could not check for existing schedule: ${existing.error}. You can create the schedule later from the Schedule tab.`,
      };
    }

    if (existing.exists) {
      return {
        success: true,
        itemsCreated: 0,
        linksCreated: 0,
        message: 'Schedule already exists for this project.',
        alreadyExists: true,
      };
    }

    const dbResult = await applyDatabaseTemplate(
      projectIdText,
      projectType,
      includeDependencyLinks,
      scheduleTemplateId
    );
    if (dbResult) return dbResult;

    const builtIn = getBuiltInSchedulePreset(projectType);
    if (builtIn) {
      return applyBuiltInPreset(projectIdText, builtIn, includeDependencyLinks);
    }

    return {
      success: false,
      itemsCreated: 0,
      linksCreated: 0,
      message: 'No schedule template found.',
      warning: `${projectType} schedule template was not found. Project was created without schedule. You can create it later from the Schedule tab.`,
    };
  } catch (err: unknown) {
    const msg = err instanceof Error ? err.message : String(err);
    console.error('[ScheduleSetup] Unexpected error:', msg);
    return {
      success: false,
      itemsCreated: 0,
      linksCreated: 0,
      message: 'Schedule setup encountered an unexpected issue.',
      warning: `Schedule setup failed unexpectedly: ${msg}. You can create the schedule later from the Schedule tab.`,
    };
  }
}

/**
 * Apply the default schedule preset for an existing project (idempotent).
 */
export async function setupScheduleForProject(
  projectId: string,
  overrides?: Partial<ScheduleSetupOptions>
): Promise<ScheduleSetupResult> {
  const { data: project, error } = await supabase
    .from('jphph_projects')
    .select('project_type')
    .eq('id', projectId)
    .maybeSingle();

  if (error || !project) {
    return {
      success: false,
      itemsCreated: 0,
      linksCreated: 0,
      message: 'Could not load project.',
      warning: error?.message || 'Project not found.',
    };
  }

  return setupProjectSchedule({
    projectId,
    projectType: project.project_type || 'Development',
    createDefaultSchedule: project.project_type === 'Development',
    includeDependencyLinks: true,
    generateInitialTimeline: false,
    ...overrides,
  });
}

/** Common renovation phases — light editable default, no dependencies or dates. */
export const LIGHT_RENOVATION_SCHEDULE_TASKS = [
  'Site check',
  'Scope confirmation',
  'Quote / approval',
  'Material ordering',
  'Site preparation',
  'Demolition',
  'Rough-in',
  'Fit-out / Installation',
  'Finishing',
  'Final check',
  'Handover',
] as const;

/**
 * Insert a small renovation schedule (11 tasks, one batch). Idempotent — skips if items exist.
 */
export async function setupLightRenovationSchedule(projectId: string): Promise<ScheduleSetupResult> {
  const projectIdText = String(projectId);

  try {
    const existing = await projectHasScheduleItems(projectIdText);
    if (existing.error) {
      return {
        success: false,
        itemsCreated: 0,
        linksCreated: 0,
        message: 'Could not verify existing schedule.',
        warning: existing.error,
      };
    }
    if (existing.exists) {
      return {
        success: true,
        itemsCreated: 0,
        linksCreated: 0,
        message: 'Schedule already exists for this project.',
        alreadyExists: true,
      };
    }

    const ganttColor = getPhaseColor('Renovation');
    const inserts = LIGHT_RENOVATION_SCHEDULE_TASKS.map((title, index) => ({
      project_id: projectIdText,
      source_template_item_id: null,
      task_no: index + 1,
      sort_order: index + 1,
      title,
      phase: 'Renovation',
      main_stage: 'Renovation',
      duration_days: 1,
      start_date: null as string | null,
      end_date: null as string | null,
      progress: 0,
      is_complete: false,
      is_included: true,
      assigned_to: null,
      assigned_role: null,
      notes: null,
      gantt_color: ganttColor,
    }));

    const { data: inserted, error: insertErr } = await batchInsertScheduleItems(inserts);
    if (insertErr) {
      return {
        success: false,
        itemsCreated: inserted.length,
        linksCreated: 0,
        message: 'Light renovation schedule setup failed.',
        warning: insertErr,
      };
    }

    return {
      success: true,
      itemsCreated: inserted.length,
      linksCreated: 0,
      message: `Created light renovation schedule with ${inserted.length} tasks.`,
    };
  } catch (err: unknown) {
    const msg = err instanceof Error ? err.message : String(err);
    return {
      success: false,
      itemsCreated: 0,
      linksCreated: 0,
      message: 'Light renovation schedule setup failed.',
      warning: msg,
    };
  }
}

export async function projectScheduleItemCount(projectId: string): Promise<number> {
  const { count } = await supabase
    .from('jphph_project_schedule_items')
    .select('id', { count: 'exact', head: true })
    .eq('project_id', String(projectId));
  return count ?? 0;
}
